import React, { useState, useEffect, useRef } from "react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { formatUtcDateTime } from "../utils/dateTime";

/**
 * Asset comments (§11g): open to any reader (a comment changes no state), removable by the
 * author — and by ADMIN+, whose moderation removals land on the activity trail. "Remove" is
 * honest: the comment leaves every read now, and the body is purged on a retention schedule.
 * Own fetch and state, deliberately outside the Behavior 360 cache invalidation.
 */
export default function AiAssetComments({ assetId }) {
  const apiCall = useApi();
  const { user } = useAuth();
  const { canAdministerOrganization } = useRBAC();
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canModerate = canAdministerOrganization(org?.organizationId);

  const [comments, setComments] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // One client key per SUBMISSION ATTEMPT, not per request: if the server wrote the comment but
  // the response was lost, the user's manual retry must reuse the key so the server returns the
  // existing comment instead of minting a duplicate. Cleared on confirmed success, or when the
  // draft materially changes (a different text is a different submission).
  const draftKeyRef = useRef(null);
  const attemptedTextRef = useRef(null);

  // Loads are token-guarded: a slow response for asset A must never land under asset B, and the
  // same guard covers the reloads after posting and removal.
  const loadSeqRef = useRef(0);
  const load = async () => {
    const seq = ++loadSeqRef.current;
    try {
      const result = await apiCall("GET", `/ai-assets/assets/${assetId}/comments`);
      if (seq !== loadSeqRef.current) return;
      setComments(result?.comments || []);
      setTruncated(!!result?.truncated);
    } catch {
      if (seq !== loadSeqRef.current) return;
      setError("Comments could not be loaded.");
    }
  };

  useEffect(() => {
    setComments([]);
    setDraft("");
    setError(null);
    draftKeyRef.current = null;
    attemptedTextRef.current = null;
    load();
    // Invalidate any in-flight load for the previous asset.
    return () => { loadSeqRef.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);

  const onDraftChange = (value) => {
    setDraft(value);
    // A materially different draft is a new submission; a retry of the same text keeps its key.
    if (attemptedTextRef.current !== null && value.trim() !== attemptedTextRef.current) {
      draftKeyRef.current = null;
      attemptedTextRef.current = null;
    }
  };

  const post = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    if (!draftKeyRef.current) {
      // randomUUID is absent in older Safari (and jsdom). The fallback must still be
      // GUID-shaped — the server binds this field as a Guid.
      draftKeyRef.current = typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : (() => {
            const hex = [...Array(32)]
              .map(() => Math.floor(Math.random() * 16).toString(16)).join("");
            return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
          })();
      attemptedTextRef.current = body;
    }
    try {
      await apiCall("POST", `/ai-assets/assets/${assetId}/comments`, {
        body,
        clientKey: draftKeyRef.current,
      });
      setDraft("");
      draftKeyRef.current = null;
      attemptedTextRef.current = null;
      await load();
    } catch (e) {
      setError(e?.response?.data?.message
        || "The comment could not be posted — retrying is safe, it will not double-post.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (comment) => {
    const own = String(comment.authorUserId) === String(user?.id);
    // Removal is irreversible in the product; one accidental click should not be enough —
    // especially for a moderator deleting someone else's words.
    const prompt = own
      ? "Remove your comment?"
      : `Remove ${comment.authorName || "another user"}'s comment? This is recorded on the asset's activity trail.`;
    if (!window.confirm(prompt)) return;
    setError(null);
    try {
      await apiCall("DELETE", `/ai-assets/comments/${comment.id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || "The comment could not be removed.");
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-border space-y-2">
      <div className="text-xs font-semibold text-content-primary">
        Comments{comments.length > 0 ? ` (${comments.length}${truncated ? "+" : ""})` : ""}
      </div>
      {truncated && (
        <div className="text-[11px] text-content-secondary">
          Showing the most recent {comments.length} — older comments are not displayed.
        </div>
      )}
      {comments.map((c) => (
        <div key={c.id} className="text-xs">
          <span className="font-medium text-content-primary">{c.authorName || "Unknown"}</span>
          <span className="text-content-secondary"> · {formatUtcDateTime(c.createdAt)}</span>
          {(String(c.authorUserId) === String(user?.id) || canModerate) && (
            <button
              onClick={() => remove(c)}
              className="ml-2 text-[11px] text-content-secondary hover:text-red-700 hover:underline"
            >
              Remove
            </button>
          )}
          <div className="text-content-primary whitespace-pre-wrap">{c.body}</div>
        </div>
      ))}
      {comments.length === 0 && !error && (
        <div className="text-xs text-content-secondary">
          No comments yet — anyone who can see this asset can add one.
        </div>
      )}
      {error && <div className="text-xs text-red-700">{error}</div>}
      <div>
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          maxLength={2000}
          placeholder="Add a comment for others looking at this asset…"
          className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary"
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-[11px] text-content-secondary">
            Do not paste credentials or secrets — comments are operational notes, not a vault.
          </span>
          <button
            onClick={post}
            disabled={busy || !draft.trim()}
            className="text-xs px-3 py-1 rounded-md border border-border bg-theme-hover text-content-primary hover:bg-theme-secondary disabled:opacity-60"
          >
            {busy ? "Posting…" : "Add comment"}
          </button>
        </div>
      </div>
    </div>
  );
}
